import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../../component-lib/Atoms/Input/Input";
import Button from "../../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import Patient from "./Patient";
import { ROLE_AND_RESPONSIBILITY } from "../../../facility-management-screen/constants";
import Visit from "./Visit";
import Contact from "./Contact";
import Task from "./Task";
import Inventory from "./Inventory";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import { getMasterRoleList } from "../../SettingsSaga";
import { componentKey } from "../../SettingsSlice";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "822px",
    width: "900px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface AddRoleDailogProps {
  isOpen: boolean;
  handleData: (data: any, checkboxData: any) => void;
  handleClose: () => void;
  isEdit?: boolean;
  defaultRoleData: any;
}

export default function AddRoleDailog({
  isOpen = false,
  handleClose,
  handleData,
  isEdit = false,
  defaultRoleData,
}: AddRoleDailogProps) {
  const defaultData = defaultRoleData?.allData;
  const dialogHeading = isEdit ? `Edit Role` : `Add Role`;

  const dispatch = useDispatch();
  const { masterRoleList } = useSelector(
    (state: any) => state[componentKey]
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(
        getMasterRoleList({
          pageNumber: 1,
          limit: 1000,
          status: "Active",
        })
      );
    }
  }, [isOpen, dispatch]);

  const roleOptions: { label: string; value: string }[] = (
    masterRoleList?.masterRoles || []
  ).map((role: any) => ({ label: role?.name, value: role?.name }));

  // PATIENT ROLES DATA
  const [rolesRowData, setRolesRowData] = useState([
    {
      roles: "Create Patient",
      description: "Allow user to create Patient",
      status: defaultData?.patient?.create || false,
    },

    {
      roles: "View Patient",
      description: "Allow user to view Patient",
      status: defaultData?.patient?.view || false,
    },
  ]);

  // VISIT ROW DATA
  const [visitRolesRowData, setVisitRolesRowData] = useState([
    {
      roles: "Create Visits",
      description: "Allow user to create Visits",
      status: defaultData?.visit?.create || false,
    },
    {
      roles: "View Visits",
      description: "Allow user to view Visits",
      status: defaultData?.visit?.view || false,
    },
  ]);

  //  CONTACT ROW DATA
  const [contactRolesRowData, setContactRolesRowData] = useState([
    {
      roles: "Create Contact",
      description: "Allow user to create Contact",
      status: defaultData?.contactDirectory?.create || false,
    },
    // {
    //   roles: "View Contact",
    //   description: "Allow user to view Contact",
    //   status: defaultData?.contactDirectory?.view || false,
    // },

    {
      roles: "Update Contact",
      description: "Allow user to update Contact",
      status: defaultData?.contactDirectory?.update || false,
    },

    {
      roles: "Delete Contact",
      description: "Allow user to delete Contact",
      status: defaultData?.contactDirectory?.delete || false,
    },
  ]);

  //  TASK ROW DATA
  const [taskRolesRowData, setTaskRolesRowData] = useState([
    {
      roles: "Create Task",
      description: "Allow user to create Task",
      status: defaultData?.task?.create || false,
    },
    {
      roles: "View Task",
      description: "Allow user to view Task",
      status: defaultData?.task?.view || false,
    },

    {
      roles: "Update Task",
      description: "Allow user to update Task",
      status: defaultData?.task?.update || false,
    },
  ]);

  //  INVENTORY ROW DATA
  const [inventoryRolesRowData, setInventoryRolesRowData] = useState([
    {
      roles: "View Inventory",
      description: "Allow user to view Inventory",
      status: defaultData?.inventory?.view || true, // Enabled by default
    },
    // {
    //   roles: "Create Inventory",
    //   description: "Allow user to create Inventory",
    //   status: defaultData?.inventory?.create || false,
    // },
    // {
    //   roles: "Update Inventory",
    //   description: "Allow user to update Inventory",
    //   status: defaultData?.inventory?.update || false,
    // },
    // {
    //   roles: "Delete Inventory",
    //   description: "Allow user to delete Inventory",
    //   status: defaultData?.inventory?.delete || false,
    // },
  ]);

  const validationSchema = Yup.object().shape({
    [ROLE_AND_RESPONSIBILITY?.ROLE_NAME]: Yup.string().required(
      "Please select role name"
    ),

    [ROLE_AND_RESPONSIBILITY?.DESCRIPTION]: Yup.string().required(
      "Description is required"
    ),
    [ROLE_AND_RESPONSIBILITY.TYPE]: Yup.string().required("Please select type"),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ROLE_AND_RESPONSIBILITY?.ROLE_NAME]: `${defaultRoleData?.roles || ""}`,
          [ROLE_AND_RESPONSIBILITY?.DESCRIPTION]: `${defaultRoleData?.description || ""}`,
          [ROLE_AND_RESPONSIBILITY?.TYPE]: `${defaultRoleData?.type || ""}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          const combinedData = [
            ...rolesRowData,
            ...visitRolesRowData,
            ...taskRolesRowData,
            ...contactRolesRowData,
            ...inventoryRolesRowData,
          ];
          try {
            // getFormData(values);
            handleData(values, combinedData);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form
              onSubmit={handleSubmit}
              className="h-full flex flex-col justify-between overflow-hidden"
            >
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                {/* <div className="pt-4">
                  <EventEHRUploadImageCard
                    imageUrl={null}
                    handleImage={(data) => {}}
                  />
                </div> */}

                <div
                //   style={{
                //     // height: deviceType === "mobile" ? "40vh" : "fit-content",
                //     overflow: "auto",
                //     display: "grid",
                //     gridTemplateColumns: "1fr 1fr",
                //     gap: "16px",
                //   }}
                >
                  <div className="pt-4 w-full flex gap-3">
                    <SelectDropdown
                      label="Type"
                      disabled={defaultRoleData?.id}
                      value={values[ROLE_AND_RESPONSIBILITY.TYPE]}
                      options={[
                        { label: "Provider", value: "provider" },
                        { label: "Staff", value: "staff" },
                      ]}
                      onChangeCb={(e) =>
                        setFieldValue(
                          ROLE_AND_RESPONSIBILITY.TYPE,
                          e.target.value
                        )
                      }
                      error={
                        touched[ROLE_AND_RESPONSIBILITY.TYPE] &&
                        Boolean(errors[ROLE_AND_RESPONSIBILITY.TYPE])
                      }
                      helperText={
                        touched[ROLE_AND_RESPONSIBILITY.TYPE]
                          ? errors[ROLE_AND_RESPONSIBILITY.TYPE]
                          : undefined
                      }
                    />

                    <SelectDropdown
                      label="Role Name"
                      disabled={defaultRoleData?.id}
                      value={values[ROLE_AND_RESPONSIBILITY.ROLE_NAME]}
                      options={
                        values[ROLE_AND_RESPONSIBILITY.ROLE_NAME] &&
                        !roleOptions.some(
                          (o) =>
                            o.value ===
                            values[ROLE_AND_RESPONSIBILITY.ROLE_NAME]
                        )
                          ? [
                              ...roleOptions,
                              {
                                label:
                                  values[ROLE_AND_RESPONSIBILITY.ROLE_NAME],
                                value:
                                  values[ROLE_AND_RESPONSIBILITY.ROLE_NAME],
                              },
                            ]
                          : roleOptions
                      }
                      onChangeCb={(e) =>
                        setFieldValue(
                          ROLE_AND_RESPONSIBILITY.ROLE_NAME,
                          e.target.value
                        )
                      }
                      error={
                        touched[ROLE_AND_RESPONSIBILITY.ROLE_NAME] &&
                        Boolean(errors[ROLE_AND_RESPONSIBILITY.ROLE_NAME])
                      }
                      helperText={
                        touched[ROLE_AND_RESPONSIBILITY.ROLE_NAME]
                          ? errors[ROLE_AND_RESPONSIBILITY.ROLE_NAME]
                          : undefined
                      }
                    />
                  </div>
                  <div className="pt-4">
                    <div>
                      <Input
                        textFieldLabel="Description"
                        // placeholder=""
                        multiline={true}
                        maxWords={250}
                        rows={4}
                        size="small"
                        name={ROLE_AND_RESPONSIBILITY.DESCRIPTION}
                        value={values[ROLE_AND_RESPONSIBILITY.DESCRIPTION]}
                        onChangeCb={(e) => {
                          setFieldValue(
                            ROLE_AND_RESPONSIBILITY.DESCRIPTION,
                            e.target.value
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched[ROLE_AND_RESPONSIBILITY.DESCRIPTION] &&
                          Boolean(errors[ROLE_AND_RESPONSIBILITY.DESCRIPTION])
                        }
                        helperText={
                          touched[ROLE_AND_RESPONSIBILITY.DESCRIPTION]
                            ? errors[ROLE_AND_RESPONSIBILITY.DESCRIPTION]
                            : undefined
                        }
                      />
                    </div>
                  </div>
                  {values[ROLE_AND_RESPONSIBILITY.ROLE_NAME] === "Supply Chain Admin" ? (
                    <div>
                      <Inventory
                        rowData={inventoryRolesRowData}
                        setInventoryRowData={setInventoryRolesRowData}
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <Patient
                          rowData={rolesRowData}
                          setRolesRowData={setRolesRowData}
                        />
                      </div>
                      <div>
                        <Visit
                          rowData={visitRolesRowData}
                          setVisitRolesRowData={setVisitRolesRowData}
                        />
                      </div>
                      <div>
                        <Contact
                          rowData={contactRolesRowData}
                          setContactRowData={setContactRolesRowData}
                        />
                      </div>
                      <div>
                        <Task
                          rowData={taskRolesRowData}
                          setTaskRowData={setTaskRolesRowData}
                        />
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {/* <div className="flex justify-end mt-10 gap-4"> */}
                {isEdit && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  //   disabled={!isValid} // Button disabled if form is invalid or submitting
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
